import { Injectable, Renderer2, DOCUMENT, inject } from '@angular/core';

import { ICultureResources, ILinkResource, ResourceConfig } from '../models/resource.model';

interface InjectOptions {
    prettyPrint?: boolean;
    indentSpaces?: number;
}

@Injectable({ providedIn: 'root' })
export class ResourceService {
    private _document = inject<Document>(DOCUMENT);

    inject(
        _renderer: Renderer2,
        _resources: ResourceConfig[],
        _insertionSelector: string,
        _options: InjectOptions = {}
    ): void {
        const { prettyPrint = false, indentSpaces = 8 } = _options;

        const insertionTarget = this._document.querySelector(_insertionSelector);
        if (!insertionTarget || !_resources.length) return;

        _resources.sort((a, b) => this._getResourceSortKey(a) - this._getResourceSortKey(b));

        const fragment = this._document.createDocumentFragment();
        const newline = prettyPrint ? this._document.createTextNode('\n') : null;
        const indent = prettyPrint ? ' '.repeat(indentSpaces) : '';

        _resources.forEach((resource) => {
            if (this._resourceExists(resource)) return;

            if (newline) fragment.appendChild(newline.cloneNode());
            if (indent) fragment.appendChild(this._document.createTextNode(indent));

            const element = _renderer.createElement(resource.tag);

            if (resource.attributes) {
                (Object.entries(resource.attributes) as [string, string | boolean][]).forEach(([key, value]) =>
                    typeof value === 'boolean'
                        ? value && _renderer.setAttribute(element, key, '')
                        : _renderer.setAttribute(element, key, value)
                );
            }

            if ('textContent' in resource && resource.textContent) {
                const formatted = this._formatInlineText(resource.textContent, indent, prettyPrint);
                _renderer.setProperty(element, 'textContent', formatted);
            }

            if (!resource.noTracking) {
                _renderer.setAttribute(element, 'data-injected', 'true');
                _renderer.setAttribute(element, 'data-persist', String(resource.persist ?? false));
            }

            fragment.appendChild(element);
        });

        if (newline) fragment.appendChild(newline.cloneNode());

        _renderer.insertBefore(insertionTarget.parentNode as Node, fragment, insertionTarget.nextSibling);
    }

    clearNonPersistentElements(renderer: Renderer2): void {
        this._document
            .querySelectorAll('[data-injected][data-persist="false"]')
            .forEach((el) => renderer.removeChild(el.parentNode as Node, el));
    }

    validate(resources: ICultureResources): string[] {
        const errors: string[] = [];

        // Rule 1: Ensure at least one `ILinkResource` in `styles` matches the pattern '/assets/fonts/*/index.css'
        const fontStylesheetPattern = /^\/assets\/fonts\/[^/]+\/index\.css$/;

        if (!resources.styles || !Array.isArray(resources.styles)) {
            errors.push('The "styles" property is missing or invalid.');
            return errors;
        }

        const hasValidFontStylesheet = resources.styles.some((style) => {
            if (style.tag === 'link') {
                const link = style as ILinkResource;
                return link.attributes.rel === 'stylesheet' && fontStylesheetPattern.test(link.attributes.href);
            }
            return false;
        });

        if (!hasValidFontStylesheet) {
            errors.push(
                'At least one stylesheet in "styles" must have an href' +
                    ' matching the pattern "/assets/fonts/*/index.css".'
            );
        }

        return errors;
    }

    private _resourceExists(res: ResourceConfig): boolean {
        if (res.tag === 'script') {
            const { id, src } = res.attributes;
            if (id && this._document.getElementById(id)) return true;
            if (src && this._document.querySelector(`script[src="${src}"]`)) return true;
            return false;
        }

        if (res.tag === 'link') {
            const { id, href } = res.attributes;
            if (id && this._document.getElementById(id)) return true;
            if (href && this._document.querySelector(`link[href="${href}"]`)) return true;
            return false;
        }

        if (res.tag === 'style') {
            const { id } = res.attributes || {};
            if (id && this._document.getElementById(id)) return true;
            return false;
        }

        if (res.tag === 'meta') {
            const { name } = res.attributes;

            if (name && this._document.querySelector(`meta[name="${name}"]`)) return true;

            return false;
        }

        if (res.tag === 'noscript') {
            const { id } = res.attributes || {};
            if (id && this._document.getElementById(id)) return true;
            return false;
        }

        return false;
    }

    private _formatInlineText(code: string, baseIndent: string, pretty: boolean): string {
        if (!pretty) return code.trim();

        const innerIndent = baseIndent + '    ';
        const trimmed = code.trim();

        const indented = trimmed
            .split('\n')
            .map((line) => innerIndent + line.trimEnd())
            .join('\n');

        return `\n${indented}\n${baseIndent}`;
    }

    private _getResourceSortKey(res: ResourceConfig): number {
        const PRIORITIES = {
            PRECONNECT: 1,
            PRELOAD: 2,
            META: 2,
            STYLESHEET: 3,
            SCRIPT: 4,
            DEFAULT: 5,
        } as const;

        if (res.tag === 'link') {
            const linkPriorities: Record<string, number> = {
                preconnect: PRIORITIES.PRECONNECT,
                preload: PRIORITIES.PRELOAD,
                modulepreload: PRIORITIES.PRELOAD,
                stylesheet: PRIORITIES.STYLESHEET,
            };

            return linkPriorities[res.attributes.rel] ?? PRIORITIES.DEFAULT;
        }

        const tagPriorities: Record<string, number> = {
            meta: PRIORITIES.META,
            style: PRIORITIES.STYLESHEET,
            script: PRIORITIES.SCRIPT,
        };

        return tagPriorities[res.tag] ?? PRIORITIES.DEFAULT;
    }
}
